import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { submitContact } from "../lib/api";
import { toast } from "sonner";

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export default function ContactSection() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      setForm(initial);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (err) {
      toast.error("Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left info */}
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#65A30D] font-bold">Contact Us</div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-3 text-ink-900"
            >
              Talk to the FUDK crew.
            </motion.h2>
            <p className="mt-6 text-neutral-700 max-w-md leading-relaxed">
              Restaurant partnerships, support, press, careers — drop us a note and our Berhampur team will get back to you.
            </p>

            <div className="mt-10 space-y-6">
              <a
                href="mailto:contactus@foodk.in"
                data-testid="contact-email-link"
                className="flex items-start gap-4 group"
              >
                <span className="bg-ink-900 text-[#84CC16] rounded-xl p-3 group-hover:bg-[#84CC16] group-hover:text-black transition-colors">
                  <Mail size={20} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-neutral-500">Email</div>
                  <div className="font-display font-bold text-lg text-ink-900">contactus@foodk.in</div>
                </div>
              </a>
              <a
                href="tel:+918926004409"
                data-testid="contact-phone-link"
                className="flex items-start gap-4 group"
              >
                <span className="bg-ink-900 text-[#84CC16] rounded-xl p-3 group-hover:bg-[#84CC16] group-hover:text-black transition-colors">
                  <Phone size={20} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-neutral-500">Phone</div>
                  <div className="font-display font-bold text-lg text-ink-900">+91 8926004409</div>
                </div>
              </a>
              <div className="flex items-start gap-4" data-testid="contact-address">
                <span className="bg-ink-900 text-[#84CC16] rounded-xl p-3">
                  <MapPin size={20} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-neutral-500">Find us</div>
                  <div className="font-display font-bold text-lg text-ink-900 leading-snug">
                    Khata No. 518/133, Plot No. 1095/1366, Bijipur Main Road, Bijipur, Berhampur, Ganjam, Odisha - 760004, India
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-neutral-50 border border-neutral-200 rounded-3xl p-6 md:p-10"
            >
              {done && (
                <div className="mb-6 flex items-center gap-3 bg-[#ECFCCB] border border-[#84CC16] text-[#365314] rounded-xl px-4 py-3 text-sm">
                  <CheckCircle2 size={18} /> Thanks! Your message reached us. We'll reply soon.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Your name" name="name" value={form.name} onChange={onChange} testid="contact-input-name" required />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testid="contact-input-email" required />
                <Field label="Phone (optional)" name="phone" value={form.phone} onChange={onChange} testid="contact-input-phone" />
                <Field label="Subject" name="subject" value={form.subject} onChange={onChange} testid="contact-input-subject" />
              </div>
              <div className="mt-5">
                <label className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-bold">Message</label>
                <textarea
                  name="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={onChange}
                  rows={5}
                  required
                  className="mt-2 w-full bg-white border border-neutral-200 rounded-xl px-4 py-3 text-sm text-ink-900 outline-none focus:border-[#84CC16] focus:ring-2 focus:ring-[#84CC16]/20 transition"
                  placeholder="Tell us how we can help…"
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading}
                className="mt-7 inline-flex items-center gap-2 bg-ink-900 hover:bg-[#262626] disabled:opacity-60 text-white font-bold rounded-full px-7 py-3.5 transition-colors"
              >
                {loading ? "Sending…" : (<>Send message <Send size={16} /></>)}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-bold">{label}{required && <span className="text-[#84CC16]">*</span>}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="mt-2 w-full bg-white border border-neutral-200 rounded-xl px-4 py-3 text-sm text-ink-900 outline-none focus:border-[#84CC16] focus:ring-2 focus:ring-[#84CC16]/20 transition"
      />
    </div>
  );
}
